export type NIATheme = "im";

export type NIARole = "user" | "assistant" | "system";

export type NIAMessage = {
  id: string;
  role: NIARole;
  content: string;
  createdAt: number;
};

export type NIAOriginContext = {
  originRoute: string;
  processId?: number | string;
  processNumber?: string;
  theme?: NIATheme | null;
};

export type NIAPayload = {
  chatInput: string;
  sessionId: string;
  theme: NIATheme;
  messages: Pick<NIAMessage, "role" | "content">[];
  originContext?: NIAOriginContext;
  processId?: number | string;
  processNumber?: string;
};

export class NIAServiceError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "NIAServiceError";
    this.status = status;
  }
}

const DEFAULT_ENDPOINT =
  import.meta.env.VITE_NIA_ENDPOINT ?? import.meta.env.VITE_N8N_ENDPOINT ?? "";

const THEME_ENDPOINTS: Record<NIATheme, string> = {
  im:
    import.meta.env.VITE_NIA_ENDPOINT_IM ??
    import.meta.env.VITE_N8N_ENDPOINT_IM ??
    "",
};

const API_KEY_HEADER =
  import.meta.env.VITE_NIA_API_KEY_HEADER?.trim() || "x-api-key";

const API_KEY = import.meta.env.VITE_NIA_API_KEY?.trim() || "";

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const extractTextFromJsonResponse = (value: unknown): string => {
  if (typeof value === "string") return value;

  if (Array.isArray(value)) {
    return value
      .map((item) => extractTextFromJsonResponse(item))
      .filter(Boolean)
      .join("\n\n");
  }

  if (value && typeof value === "object") {
    const objectValue = value as Record<string, unknown>;
    const knownFields = [
      "output",
      "answer",
      "content",
      "text",
      "message",
      "response",
      "data",
    ];

    for (const key of knownFields) {
      if (!(key in objectValue)) continue;

      const extracted = extractTextFromJsonResponse(objectValue[key]);
      if (extracted.trim()) return extracted;
    }

    for (const nestedValue of Object.values(objectValue)) {
      const extracted = extractTextFromJsonResponse(nestedValue);
      if (extracted.trim()) return extracted;
    }
  }

  return "";
};

const unwrapJsonString = (text: string) => {
  const trimmed = text.trim();

  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return text;
    }
  }

  return text;
};

export const getNIAEndpoint = (theme: NIATheme) => {
  const endpoint = THEME_ENDPOINTS[theme];
  return endpoint.trim().length > 0 ? endpoint : DEFAULT_ENDPOINT;
};

export const readNIAResponse = async (response: Response) => {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => null);
    const extracted = extractTextFromJsonResponse(payload);

    if (extracted.trim()) {
      return extracted;
    }
  }

  return await response.text();
};

export const normalizeNIAResponse = (text: string) => {
  let content = unwrapJsonString(text);

  if (typeof content !== "string") {
    content = String(content);
  }

  if (!content.trim()) {
    return content;
  }

  content = content
    .replace(/\\r\\n/g, "\r\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .trim();

  if (
    (content.startsWith("{") && content.endsWith("}")) ||
    (content.startsWith("[") && content.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(content);
      const extracted = extractTextFromJsonResponse(parsed);

      if (extracted.trim()) {
        content = extracted.trim();
      }
    } catch {
      // Ignora se a resposta não for JSON válido.
    }
  }

  return content;
};

export const postNIAMessage = async ({
  accessToken,
  endpoint,
  payload,
  signal,
}: {
  accessToken: string;
  endpoint: string;
  payload: NIAPayload;
  signal?: AbortSignal;
}) => {
  if (!endpoint) {
    throw new NIAServiceError("Endpoint da NIA não está configurado.");
  }

  const headers = {
    ...JSON_HEADERS,
    Authorization: `Bearer ${accessToken}`,
    ...(API_KEY ? {[API_KEY_HEADER]: API_KEY} : {}),
  };

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const responseText = await response.text().catch(() => "");

    throw new NIAServiceError(
      responseText || `Falha ao consultar a NIA (${response.status}).`,
      response.status,
    );
  }

  return response;
};
